import {
  type CylinderSurface,
  outlineBounds,
  type SizeMm,
  type SurfacePoint,
} from '@repo/api-contracts';
import { cylinderParamsOf } from '@repo/design-renderer';
import { type PhotoSize, photoPercentRectToPx, type Rect } from '@repo/warp-engine';
import { type PhotoDimensions, rectPoints } from './flat-surface-mapping';

/** 관리자 사진 크기({widthPx, heightPx})를 엔진 사진 크기({width, height})로 바꾼다. 그대로 넘기면 NaN 이 된다. */
const toEnginePhoto = ({ widthPx, heightPx }: PhotoDimensions): PhotoSize => ({
  width: widthPx,
  height: heightPx,
});

/**
 * 저장한 보이는 사각형(points, 사진 %)을 사진 px 사각형으로 바꾼다.
 * 가로 범위는 투영 폭이고, 세로 범위는 가운데 열의 위·아래 경계다(ADR-0007 부록 A).
 */
const visibleRectPx = (
  [topLeft, topRight, , bottomLeft]: CylinderSurface['points'],
  photo: PhotoDimensions,
): Rect =>
  photoPercentRectToPx(
    {
      left: topLeft.x,
      top: topLeft.y,
      width: topRight.x - topLeft.x,
      height: bottomLeft.y - topLeft.y,
    },
    toEnginePhoto(photo),
  );

/**
 * 저장한 곡률 비율을 사진 px 로 보인다(결정 Q1). px = 비율 × 보이는 사각형 높이 px.
 * 분모는 어댑터가 upSide·downSide 를 만들 때 쓰는 area.height 와 같은 보이는 사각형 높이 px 다.
 * photo 는 기준 사진 색상의 그 면 사진 크기다.
 */
export const curvePxOf = (
  ratio: number,
  points: CylinderSurface['points'],
  photo: PhotoDimensions,
): number => ratio * visibleRectPx(points, photo).height;

/**
 * 운영자가 사진 px 로 넣은 곡률을 저장 비율로 바꾼다(결정 Q1). 비율 = px ÷ 보이는 사각형 높이 px.
 * photo 는 기준 사진 색상의 그 면 사진 크기다.
 * 사각형 높이가 0 이하이거나 결과가 유한수가 아니면 비율을 정할 수 없어 null 이다.
 */
export const curveRatioOf = (
  px: number,
  points: CylinderSurface['points'],
  photo: PhotoDimensions,
): number | null => {
  const heightPx = visibleRectPx(points, photo).height;
  const ratio = px / heightPx;

  return heightPx > 0 && Number.isFinite(ratio) ? ratio : null;
};

export { cylinderParamsOf };

const copyOutline = (points: readonly SurfacePoint[]): NonNullable<CylinderSurface['outline']> => ({
  points: points.map((point) => ({ x: point.x, y: point.y })),
});

/**
 * 원통 상품의 '상품 추출하기'로 새 원통 표면을 만든다(결정 Q2). 보이는 사각형은 윤곽의 외접 사각형이고 곡률은 0 이다.
 * frame 은 상품 실측의 원통 프레임(measurementFrameOf)이고 반올림하지 않은 값을 그대로 복사한다.
 */
export const cylinderSurfaceFromOutline = (
  id: string,
  frame: SizeMm,
  points: readonly SurfacePoint[],
): CylinderSurface => {
  const [topLeft, , bottomRight] = outlineBounds(points);

  return {
    id,
    type: 'cylinder',
    realSize: { widthMm: frame.widthMm, heightMm: frame.heightMm },
    points: rectPoints(topLeft.x, topLeft.y, bottomRight.x, bottomRight.y),
    topCurveRatio: 0,
    bottomCurveRatio: 0,
    outline: copyOutline(points),
  };
};

/**
 * 같은 면에 원통 표면이 있을 때의 '다시 추출하기'(결정 Q2) — 윤곽만 바꾼다. 원통 윤곽은 실루엣 참고용이라
 * 보이는 사각형과 묶이지 않는다(ADR-0007 D7). id·보이는 사각형·곡률·실측을 그대로 두므로 배치·합성 설정도 유지된다.
 */
export const replaceCylinderOutline = (
  surface: CylinderSurface,
  points: readonly SurfacePoint[],
): CylinderSurface => ({ ...surface, outline: copyOutline(points) });
